import { useHistory, useLocation } from "react-router-dom";

/**
 * Custom hook for managing query parameters in React Router v5 with type-safe getters and setters.
 * @template T - The shape of the query parameters object.
 * @returns {[Function, (newParams: Partial<T>) => void]} - A type-safe getter and a setter function for query parameters.
 */
function useQueryParams<T extends Record<string, string | null>>() {
  const location = useLocation();
  const history = useHistory();

  const queryParams = new URLSearchParams(location.search);

  /**
   * Type-safe getter for query parameters.
   * @param key - The key of the query parameter to retrieve.
   * @returns The value of the query parameter as a string or `null` if it doesn't exist.
   */
  const getQueryParam = (key: keyof T): string | null => {
    return queryParams.get(key as string);
  };

  /**
   * Updates the query parameters in the URL.
   * @param newParams - An object containing query parameters to update or remove.
   */
  const setQueryParams = (newParams: Partial<T>) => {
    const updatedParams = new URLSearchParams(location.search);

    // Update or delete parameters based on the `newParams` object.
    Object.keys(newParams).forEach((key) => {
      const value = newParams[key as keyof T];
      if (value === null || value === undefined) {
        updatedParams.delete(key); // Remove the parameter if value is null/undefined.
      } else {
        updatedParams.set(key, value); // Set/Update the parameter.
      }
    });

    // Push the updated search string to the history.
    history.push({
      pathname: location.pathname,
      search: updatedParams.toString(),
    });
  };

  return [getQueryParam, setQueryParams] as const;
}

export default useQueryParams;
